import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { PersistedClient } from "@tanstack/react-query-persist-client";
import { get, set, del, createStore } from "idb-keyval";

const queryStore = createStore("production-hub-cache", "tanstack-query");

const EXCLUDED_QUERY_KEY_PREFIXES = new Set([
  "bridge-status",
  "bridge-connections",
  "bridge-config",
  "device-state",
  "network-coverage",
  "notifications",
]);

export function shouldDehydrateQuery(query: {
  queryKey: readonly unknown[];
}): boolean {
  const prefix = query.queryKey[0];
  if (typeof prefix === "string" && EXCLUDED_QUERY_KEY_PREFIXES.has(prefix)) {
    return false;
  }
  return true;
}

function deserialize(cachedString: string): PersistedClient {
  const parsed = JSON.parse(cachedString) as PersistedClient;
  // Promises can't survive JSON serialization — they become `{}`.
  // Strip them so hydrate() doesn't call .then() on a plain object.
  parsed.clientState?.queries?.forEach((query) => {
    delete (query as unknown as Record<string, unknown>).promise;
  });
  return parsed;
}

export const asyncStoragePersister = createAsyncStoragePersister({
  key: `production-hub-query-cache:${import.meta.env.VITE_CACHE_BUSTER || ""}`,
  storage: {
    getItem: async (key: string) => {
      const value = await get<string>(key, queryStore);
      return value ?? null;
    },
    setItem: (key: string, value: string) => set(key, value, queryStore),
    removeItem: (key: string) => del(key, queryStore),
  },
  deserialize,
});
